<template>
  <div class="chart-renderer">
    <div class="chart-renderer__area">
      <template v-if="!isEmpty">
        <AnalyticsChartHorizontalbar
          v-if="chartType === CHART_TYPE.HORIZONTALBAR"
          :chart-data="chartData"
          class="chart-renderer__chart"
        />
        <AnalyticsChartHorizontalPipe
          v-if="chartType === CHART_TYPE.PIPE"
          :chart-data="chartData"
          class="chart-renderer__chart"
        />
        <AnalyticsChartHorizontalFunnel
          v-if="chartType === CHART_TYPE.FUNNEL"
          :chart-data="chartData"
          class="chart-renderer__chart"
        />
        <AnalyticsChartDoughnut
          v-if="chartType === CHART_TYPE.DOUGHNUT"
          :chart-data="chartData"
          :show-datalabels="showDatalabels"
          :is-clickable="isClickable"
          class="chart-renderer__chart"
          @open-drilldown="eventBody => $emit('open-drilldown', eventBody)"
        />
        <AnalyticsChartBar
          v-if="chartType === CHART_TYPE.BAR"
          :is-stacked="isStacked"
          :is-time-query="isTimeQuery"
          :show-datalabels="showDatalabels"
          :is-multi-measures="isMultiMeasures"
          :chart-data="chartData"
          :is-clickable="isClickable"
          class="chart-renderer__chart"
          @open-drilldown="eventBody => $emit('open-drilldown', eventBody)"
        />
      </template>
      <div
        v-else
        class="chart-renderer__empty"
      >
        <i class="empty__icon icon-error" />
        <p class="empty__text">
          {{ errorMessage }}
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { CHART_TYPE } from '~/constants/renderTypes'
import {
  getDefaultMeasureTranslation,
  getFunnelMeasureTranslation
} from '~/services/explore'
import { MemberFormat } from '~/types/analyticsFormat'
import type { Dimension, Measure } from '~/types/cube'
import {
  compareValuesAlphabetically,
  getFormattedDimensionValue
} from '~/services/analytics'
import type {
  QueryResults,
  ResultSetItem,
  ResultSetItemValue
} from '~/types/query'
import type { ALL_MAIN_TIME_DIMENSION_TITLES } from '~/constants/timeDimension'
import {
  hasMainTimeDimension,
  isPeriodInFuture
} from '~/services/timeDimension'
import type {
  ChartData,
  OpenDrilldownEventBody,
  DataSet
} from '~/types/analytics'
import { buildObjectFromEntries } from '~/types/utils'

const CHART_TYPES_WITHOUT_CUSTOM_SORTING = [
  CHART_TYPE.FUNNEL,
  CHART_TYPE.PIPE,
  CHART_TYPE.HORIZONTALBAR
]

type DatasetKey = {
  measure: Measure
  measureIndex: number
  groupValue: ResultSetItemValue | undefined
}

type Category = {
  formattedValue: string
  matchingResultSetItems: ResultSetItem[]
  totalsByMeasureTitle: { [measureTitle: string]: number }
  value: ResultSetItemValue
  isInFuture?: boolean
}

export default {
  name: 'RendererChart',
  props: {
    chartType: {
      type: String as PropType<CHART_TYPE>,
      required: true
    },
    queryResults: {
      type: Object as PropType<QueryResults>,
      required: true
    },
    showDatalabels: {
      type: Boolean,
      default: false
    },
    sortByAlphabeticalOrder: {
      type: Boolean,
      default: false
    },
    displayValuesAsPercent: {
      type: Boolean,
      default: false
    },
    isClickable: {
      type: Boolean,
      default: false
    }
  },
  emits: {
    'open-drilldown': (_: OpenDrilldownEventBody) => true
  },
  setup() {
    return { CHART_TYPE }
  },
  computed: {
    errorMessage() {
      if (this.isEmpty) {
        return this.$t('dashboard.error.empty.chart')
      }
      return this.$t('dashboard.error.unknown')
    },
    isStacked() {
      return this.queryResults.query.dimensions.length > 1
    },
    isMultiMeasures() {
      return this.queryResults.query.measures.length > 1
    },
    isTimeQuery() {
      return hasMainTimeDimension(this.queryResults.query.dimensions)
    },
    isEmpty() {
      const isMeasureEqualToZero = !this.queryResults.data.reduce(
        (sum, data) =>
          sum + (data[this.queryResults.query.measures[0].name] as number),
        0
      )

      if (this.isTimeQuery) {
        return isMeasureEqualToZero
      }
      return this.queryResults.data.length === 0 || isMeasureEqualToZero
    },
    chartData(): ChartData {
      const resultSetItems = this.queryResults.data
      const measures = this.queryResults.query.measures

      // All charts have a primary dimension except for pipes and funnels
      const primaryDimension = this.queryResults.query.dimensions[0]
      const groupDimension = this.queryResults.query.dimensions[1]

      const primaryDimensionValues =
        primaryDimension &&
        this.getDimensionValues(primaryDimension.name, resultSetItems)

      const groupDimensionValues =
        groupDimension &&
        this.getDimensionValues(groupDimension.name, resultSetItems)

      // Categories define the different primary dimension values for which data is being measured and compared. In a funnel or a pipe there are no categories because we do not use a primary dimension
      const categories =
        primaryDimension && primaryDimensionValues
          ? primaryDimensionValues.map(primaryDimensionValue =>
              this.getCategory(
                primaryDimensionValue,
                resultSetItems,
                primaryDimension,
                measures,
                this.isTimeQuery
              )
            )
          : undefined

      const sortedCategories = categories
        ?.sort((category1, category2) => {
          if (this.chartType === CHART_TYPE.HORIZONTALBAR) {
            return compareValuesAlphabetically(category2.value, category1.value)
          } else if (this.sortByAlphabeticalOrder) {
            return compareValuesAlphabetically(category1.value, category2.value)
          } else {
            return (
              category2.totalsByMeasureTitle[measures[0].shortTitle]! -
              category1.totalsByMeasureTitle[measures[0].shortTitle]!
            )
          }
        })
        .filter(category => {
          // Remove categories with total = 0
          // TODO: We should not remove categories if time query or if the measures values can be negative
          return Object.values(category.totalsByMeasureTitle).some(
            totalByMeasure => totalByMeasure !== 0
          )
        })

      const chartLabels =
        sortedCategories?.map(({ value, formattedValue, isInFuture }) => ({
          value,
          formattedValue,
          isInFuture
        })) ?? []

      const datasetKeys: DatasetKey[] = groupDimensionValues
        ? measures
            .map((measure, measureIndex) =>
              groupDimensionValues.map(groupDimensionValue => ({
                groupValue: groupDimensionValue,
                measure,
                measureIndex
              }))
            )
            .flat()
        : measures.map((measure, measureIndex) => ({
            measure,
            measureIndex,
            groupValue: undefined
          }))

      const datasets = datasetKeys
        .map(datasetKey => {
          const datasetData = this.getDatasetData(
            datasetKey,
            sortedCategories,
            groupDimension,
            resultSetItems,
            this.displayValuesAsPercent
          )

          const groupValueLabel =
            groupDimension &&
            getFormattedDimensionValue(datasetKey.groupValue, groupDimension)

          const measureLabel = this.getMeasureTranslation(datasetKey.measure)

          const label = groupDimension
            ? measures.length > 1
              ? `${datasetKey.measureIndex + 1}. ${groupValueLabel!}`
              : groupValueLabel!
            : measureLabel

          return {
            key:
              groupDimension && datasetKey.groupValue !== null
                ? `${datasetKey.groupValue},${datasetKey.measure.shortTitle}`
                : datasetKey.measure.shortTitle,
            label,
            groupValue: datasetKey.groupValue,
            groupValueLabel,
            measureLabel,
            measureTitle: datasetKey.measure.shortTitle,
            data: datasetData,
            format: this.displayValuesAsPercent
              ? MemberFormat.PERCENT
              : datasetKey.measure.meta.format
          }
        })
        .filter(dataset => dataset.data.some(value => value !== null))

      const orderedDatasets = CHART_TYPES_WITHOUT_CUSTOM_SORTING.includes(
        this.chartType
      )
        ? datasets
        : this.sortDatasets(datasets)

      return {
        labels: chartLabels,
        datasets: orderedDatasets
      }
    },
    areDatasetsGroupedByACountableValue() {
      if (!this.isStacked) {
        return false
      }

      const groupDimension = this.queryResults.query.dimensions[1]

      return groupDimension && groupDimension.type === 'number'
    }
  },
  methods: {
    getDimensionValues(dimensionName: string, resultSetItems: ResultSetItem[]) {
      return [
        ...new Set(
          resultSetItems.map(
            resultSetItem => resultSetItem[dimensionName] as ResultSetItemValue
          )
        )
      ]
    },
    getCategory(
      primaryDimensionValue: ResultSetItemValue,
      resultSetItems: ResultSetItem[],
      primaryDimension: Dimension,
      measures: Measure[],
      isTimeQuery: boolean
    ): Category {
      const matchingResultSetItems = resultSetItems.filter(
        resultSetItem =>
          resultSetItem[primaryDimension.name] === primaryDimensionValue
      )

      return {
        formattedValue: getFormattedDimensionValue(
          primaryDimensionValue,
          primaryDimension
        ),
        matchingResultSetItems,
        totalsByMeasureTitle: buildObjectFromEntries(
          measures.map(measure => [
            measure.shortTitle,
            matchingResultSetItems.reduce<number>(
              (totalForMeasure, resultSetItem) =>
                totalForMeasure +
                ((resultSetItem[measure.name] as number | null) ?? 0),
              0
            )
          ])
        ),
        value: primaryDimensionValue,
        ...(isTimeQuery && primaryDimensionValue !== null
          ? {
              isInFuture: isPeriodInFuture(
                primaryDimensionValue as string,
                primaryDimension.shortTitle as (typeof ALL_MAIN_TIME_DIMENSION_TITLES)[number]
              )
            }
          : {})
      }
    },
    getDatasetData(
      datasetKey: DatasetKey,
      sortedCategories: Category[] | undefined,
      groupDimension: Dimension | undefined,
      resultSetItems: ResultSetItem[],
      displayValuesAsPercent: boolean
    ) {
      if (!sortedCategories) {
        // This case is only for funnel and pipe charts
        return [
          (resultSetItems[0]?.[datasetKey.measure.name] as
            | number
            | null
            | undefined) || null
        ]
      }

      const defaultDatasetData = sortedCategories.map(category => {
        const resultSetItem = groupDimension
          ? category.matchingResultSetItems.find(
              resultSetItem =>
                resultSetItem[groupDimension.name] === datasetKey.groupValue
            )
          : category.matchingResultSetItems[0]

        // We cast the value to null if it's equal to 0 to hide it in the final chart
        const defaultValue =
          (resultSetItem?.[datasetKey.measure.name] as number | null) || null

        if (displayValuesAsPercent && groupDimension && defaultValue !== null) {
          const totalForMeasureAndPrimaryDimension =
            category.totalsByMeasureTitle[datasetKey.measure.shortTitle]!

          return totalForMeasureAndPrimaryDimension !== 0
            ? (defaultValue * 100) / totalForMeasureAndPrimaryDimension
            : defaultValue
        } else {
          return defaultValue
        }
      })

      if (displayValuesAsPercent && !groupDimension) {
        const totalForMeasure = defaultDatasetData.reduce<number>(
          (total, value) => total + (value ?? 0),
          0
        )

        return defaultDatasetData.map(value =>
          totalForMeasure !== 0 && value !== null
            ? (value * 100) / totalForMeasure
            : value
        )
      } else {
        return defaultDatasetData
      }
    },
    sortDatasets(datasets: DataSet[]): DataSet[] {
      if (this.areDatasetsGroupedByACountableValue) {
        return datasets.sort(
          (n1, n2) => (n1.groupValue as number) - (n2.groupValue as number)
        )
      }
      return datasets.sort((n1, n2) =>
        compareValuesAlphabetically(n1.data[0]!, n2.data[0]!)
      )
    },
    getMeasureTranslation(measure: Measure) {
      if (
        this.chartType === CHART_TYPE.FUNNEL ||
        this.chartType === CHART_TYPE.PIPE
      ) {
        return getFunnelMeasureTranslation(measure)
      } else {
        return getDefaultMeasureTranslation(measure)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.chart-renderer {
  height: 100%;
  display: flex;
  position: relative;

  &__area {
    height: 100%;
    width: 100%;
    position: relative;
    overflow: hidden;
  }

  &__chart {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
  }

  &__empty {
    height: calc(100% - #{$margin-large});
    width: 200px;
    margin: auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .empty {
      &__icon {
        @include font-icon;
        @include font-size(24px);
        color: $text-secondary;
        margin-bottom: 4px;
      }

      &__text {
        @include font-text;
        @include font-size($font-size-mini);
        color: $text-secondary;
        text-align: center;
        margin-bottom: 24px;
      }
    }
  }

  &__legend {
    display: flex;
    flex-direction: column;
    overflow-y: scroll;
    min-height: 160px;
    height: 160px;

    :deep(li) {
      display: flex;
      margin-bottom: 6px;

      &:last-of-type {
        margin-bottom: 16px;
      }

      div {
        display: flex;
        flex-shrink: 0;
        width: 42px;
        height: 16px;
        margin-right: 8px;
      }

      span {
        @include font-text;
        @include font-size($font-size-mini);
      }
    }
  }
}
</style>
